import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Shield, Users, Clock, ArrowRight } from "lucide-react";
import GlobalETicketsWidget from "../components/GlobalETicketsWidget";
import { useSiteImages } from "../hooks/useSiteImages";

export default function ETicketsPage() {
  const { getImage } = useSiteImages();
  const features = [
    { icon: <Zap />, title: "Instant Delivery", description: "Tickets delivered directly to buyers' phones" },
    { icon: <Shield />, title: "Secure Scanning", description: "QR code validation prevents fraud" },
    { icon: <Users />, title: "Real-Time Analytics", description: "Track sales and attendance live" },
    { icon: <Clock />, title: "24/7 Support", description: "We're here when you need us" },
  ];

  return (
    <div data-testid="e-tickets-page" className="pt-20">
      <section className="py-24 bg-[#0F2137] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"><div className="absolute top-0 right-0 w-96 h-96 bg-[#D90429] rounded-full blur-3xl" /></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="section-kicker">E-Ticketing Solutions</span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Seamless Digital Ticketing for Your Events</h1>
              <p className="text-xl text-gray-300 mb-8">Powered by Global E-Tickets, Lane4 provides a complete ticketing solution that makes selling and managing tickets effortless.</p>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Partner With Us <ArrowRight size={20} /></Link>
            </div>
            <div>
              <img src={getImage("etickets_hero")} alt="Seamless Digital Ticketing" className="rounded-xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="section-kicker">Why Choose Our Ticketing</span>
            <h2 className="text-4xl font-bold text-[#0F2137]">Event-Day Made Easy</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="text-center p-6 bg-[#F8F9FA] rounded-xl">
                <div className="w-14 h-14 bg-[#D90429]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#D90429]">{feature.icon}</div>
                <h3 className="font-bold text-[#0F2137] mb-2">{feature.title}</h3>
                <p className="text-[#495057] text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#F8F9FA]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="section-kicker">Buy Tickets</span>
            <h2 className="text-4xl font-bold text-[#0F2137]">Lane4 E-Tickets</h2>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-8 shadow-lg" data-testid="ticket-widget-placeholder">
            <GlobalETicketsWidget src={getImage("etickets_embed_url")} title="Lane4 Tickets" />
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F2137]">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Event Organizer?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">Let Lane4 handle your ticketing so you can focus on creating an amazing event experience.</p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Contact Sales <ArrowRight size={20} /></Link>
        </div>
      </section>
    </div>
  );
}
